import { useMemo } from "react";

import useGetClient, {
  ClientInfo,
} from "@fartherfinance/frontend/api/Entity/hooks/useGetClient";
import useGetFamilialRelation from "@fartherfinance/frontend/api/Entity/hooks/useGetFamilialRelation";
import { ClientId, HookResult } from "@fartherfinance/frontend/api/Types";

import { getFamilialRelationId } from "@src/constants/relationship";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";

export interface ClientDetails {
  firstName: string;
  fullName: string;
  clientId: ClientId;
  dob: string | null;
}

export interface ClientAndSpouseDetails {
  client: ClientDetails;
  spouse: ClientDetails | undefined;
}

export default function useGetClientAndSpouseDetails(
  clientId: ClientId
): HookResult<ClientAndSpouseDetails> {
  const auth = useRequestAuth();

  const familialRelationId = getFamilialRelationId("Spouse");

  const familialRelation = useGetFamilialRelation(
    clientId,
    familialRelationId,
    auth
  );

  const spouseId = familialRelation.data?.clientIds?.[0] ?? null;

  const clientProfile = useGetClient(clientId, auth);
  const spouseProfile = useGetClient(spouseId, auth);

  const formatClientSpouseProfile = ({
    name,
    clientId,
    dateOfBirth,
  }: ClientInfo): ClientDetails => {
    const { first: firstName = "", last: lastName } = name ?? {};

    return {
      firstName,
      fullName: lastName ? `${firstName} ${lastName}` : firstName ?? "",
      clientId,
      dob: dateOfBirth,
    };
  };

  return useMemo((): HookResult<ClientAndSpouseDetails> => {
    if (clientProfile.isLoading || clientProfile.hasError) {
      return clientProfile;
    }

    if (familialRelation.isLoading || familialRelation.hasError) {
      return familialRelation;
    }

    if (spouseId !== null && spouseProfile.isLoading) {
      return spouseProfile;
    }

    const formattedClient = formatClientSpouseProfile(clientProfile.data);

    if (
      spouseId === null ||
      spouseProfile.data === undefined ||
      spouseProfile.hasError
    ) {
      return {
        ...clientProfile,
        data: {
          client: formattedClient,
          spouse: undefined,
        },
      };
    }

    const formattedSpouse = formatClientSpouseProfile(spouseProfile.data);

    return {
      ...clientProfile,
      data: {
        client: formattedClient,
        spouse: formattedSpouse,
      },
    };
  }, [clientProfile, familialRelation, spouseId, spouseProfile]);
}
